import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Home } from "lucide-react";
import { motion } from "framer-motion";
import { useApp, formatEUR } from "@/context/AppContext";
import { getCheckoutStatus } from "@/lib/api";
import { Button } from "@/components/ui/button";

const MAX_POLLS = 8;

export default function Success() {
  const { t, lang, clearCart } = useApp();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | failed
  const [data, setData] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let polls = 0;
    let timer;
    const poll = async () => {
      try {
        const res = await getCheckoutStatus(sessionId);
        setData(res);
        if (res.payment_status === "paid") {
          setState("paid");
          if (!cleared.current) { clearCart(); cleared.current = true; }
          return;
        }
      } catch { /* keep polling */ }
      polls += 1;
      if (polls >= MAX_POLLS) { setState("failed"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]); // eslint-disable-line

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-xl flex-col items-center justify-center px-6 py-16 text-center" data-testid="success-page">
      {state === "polling" && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center gap-4">
          <Loader2 className="h-14 w-14 animate-spin text-cea-accent" />
          <p className="text-lg font-semibold text-cea-ink" data-testid="success-processing">{t.success.processing}</p>
        </motion.div>
      )}

      {state === "paid" && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center gap-4" data-testid="success-paid">
          <CheckCircle2 className="h-16 w-16 text-green-500" />
          <h1 className="text-3xl font-extrabold tracking-tight text-cea-ink">{t.success.title}</h1>
          <p className="text-slate-500">{t.success.sub}</p>
          {data?.amount != null && (
            <p className="text-lg font-bold text-cea-navy">{formatEUR(data.amount)}</p>
          )}
          <p className="text-xs text-slate-400">{t.success.ref}: {sessionId?.slice(-12)}</p>
          <Button asChild className="mt-2 rounded-full bg-cea-accent text-white hover:bg-cea-navy">
            <Link to="/" data-testid="success-home"><Home className="mr-2 h-4 w-4" />{t.success.back}</Link>
          </Button>
        </motion.div>
      )}

      {state === "failed" && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center gap-4" data-testid="success-failed">
          <XCircle className="h-16 w-16 text-red-400" />
          <p className="text-lg font-semibold text-cea-ink">{t.success.failed}</p>
          <Button asChild variant="outline" className="rounded-full">
            <Link to="/cart" data-testid="failed-back-cart">{t.cart.title}</Link>
          </Button>
        </motion.div>
      )}
    </div>
  );
}
